import { PartialDeep } from 'type-fest';

import UserStore from 'stores/UserStore';
import { Campaign } from 'stores/UserStore/UserStore.types';

import { Theme } from './themes.types';

/**
 * Get active campaign that has the flag in campaignDetails object.
 *
 * Flag should be present inside themeFlags field in campaign details object
 * (e.g. `"themeFlags": ["HAPPY_HOUR_ON_CAM_ANIMATION"]`)
 * @param flag The flag
 * @returns Active campaign with the flag
 */
export const getCampaignWithFlag = (flag: string): Campaign => UserStore.user.activeCampaigns?.find(
  (campaign) => campaign.campaignDetails?.themeFlags?.toJS().includes(flag)
);

type MapObject = {[key: string]: any};

/**
 * Return copy of the object without field that are undefined
 * @param obj Source object
 * @returns Resulting object
 */
export const removeEmpty = <T extends MapObject>(obj: T): Partial<T> => {
  const newObject = Object.keys(obj).reduce((result, key) => {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }

    return result;
  }, {} as MapObject);

  return newObject as Partial<T>;
};

type GetThemePartCallback<ThemePartKey extends keyof Theme, IsPartial extends boolean> =
  (campaign: Campaign) => IsPartial extends true ? PartialDeep<Theme[ThemePartKey]> : Theme[ThemePartKey];

/**
 * Get particular theme part config
 * IsPartial=true in the generic type means that we have default values for this theme part
 * @param themeFlag Theme flag that should be checked for presence
 * @param callback Callback that takes campaign with the Theme flag and returns the theme part
 * @returns Theme part
 */
export const getThemePart = <ThemePartKey extends keyof Theme, IsPartial extends boolean = false>(
  themeFlag: string,
  callback: GetThemePartCallback<ThemePartKey, IsPartial>
) => {
  const campaign = getCampaignWithFlag(themeFlag);

  return campaign ? callback(campaign) : undefined;
};
